import { Theme } from "@mui/material/styles";
import { ColorSchema } from "src/theme/palette";
import { ChatSender } from "src/@types/chat-message";

// ----------------------------------------------------------------------
// Aparência das bolhas, no espírito do WhatsApp, funcionando em light e dark.
// ----------------------------------------------------------------------

export const WA_COLORS = {
  wallpaperLight: "#EFE7DE",
  wallpaperDark: "#0B141A",
  incomingLight: "#FFFFFF",
  incomingDark: "#202C33",
  outgoingLight: "#D9FDD3",
  outgoingDark: "#005C4B",
};

/** Padrão sutil de fundo, embutido como data URI (sem request externo). */
const PATTERN = (stroke: string) =>
  `<svg xmlns="http://www.w3.org/2000/svg" width="120" height="120" viewBox="0 0 120 120">
     <g fill="none" stroke="${stroke}" stroke-width="1.1" stroke-linecap="round">
       <path d="M14 26h12M20 20v12"/><path d="M86 74l8 8M94 74l-8 8"/>
       <circle cx="58" cy="46" r="5"/><path d="M30 92q8-10 16 0"/>
       <path d="M96 22q6 6 0 12"/><path d="M8 70h10"/>
     </g>
   </svg>`;

export const chatWallpaperSx = (theme: Theme) => {
  const isDark = theme.palette.mode === "dark";

  return {
    bgcolor: isDark ? WA_COLORS.wallpaperDark : WA_COLORS.wallpaperLight,
    // encodeURIComponent é obrigatório: `#` cru dentro de url() quebra o CSS.
    backgroundImage: `url("data:image/svg+xml;charset=utf8,${encodeURIComponent(
      PATTERN(isDark ? "rgba(255,255,255,0.045)" : "rgba(11,20,26,0.05)")
    )}")`,
    backgroundRepeat: "repeat",
    backgroundSize: "260px 260px",
  } as const;
};

export const bubbleColor = (theme: Theme, isClient: boolean) => {
  const isDark = theme.palette.mode === "dark";

  if (isClient) return isDark ? WA_COLORS.incomingDark : WA_COLORS.incomingLight;
  return isDark ? WA_COLORS.outgoingDark : WA_COLORS.outgoingLight;
};

export const bubbleSx = (
  theme: Theme,
  {
    isClient,
    isFirstOfBlock,
    compact,
  }: { isClient: boolean; isFirstOfBlock: boolean; compact?: boolean }
) =>
  ({
    position: "relative",
    // Mídia ocupa a bolha quase inteira, como no WhatsApp.
    px: compact ? 0.5 : 1.25,
    py: compact ? 0.5 : 0.875,
    maxWidth: { xs: "85%", md: 460 },
    // Espaço mínimo pra hora não colidir com textos muito curtos.
    minWidth: compact ? 0 : 96,
    wordBreak: "break-word",
    bgcolor: bubbleColor(theme, isClient),
    color: theme.palette.mode === "dark" ? "common.white" : "grey.900",
    boxShadow: "0 1px 0.5px rgba(11,20,26,0.13)",
    borderRadius: "8px",
    // Canto quadrado do lado do rabicho, só na primeira bolha do bloco.
    ...(isFirstOfBlock && isClient && { borderTopLeftRadius: 0 }),
    ...(isFirstOfBlock && !isClient && { borderTopRightRadius: 0 }),
  } as const);

// ----------------------------------------------------------------------
// Cor determinística da etiqueta de autoria
// ----------------------------------------------------------------------

/** Ordem fixa — mudar a ordem troca a cor de todos os atendentes. */
const HASH_PALETTE: ColorSchema[] = [
  "secondary",
  "info",
  "success",
  "warning",
  "error",
  "primary",
];

const FIXED: Partial<Record<ChatSender["kind"], ColorSchema>> = {
  assistant: "info",
  integration: "warning",
  whatsapp: "success",
};

export function senderColor(theme: Theme, sender: ChatSender): string {
  let schema = FIXED[sender.kind];

  if (!schema) {
    const seed = (sender.label || "").toLowerCase();
    let hash = 0;
    for (let i = 0; i < seed.length; i += 1) {
      hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
    }
    schema = HASH_PALETTE[hash % HASH_PALETTE.length];
  }

  const color = theme.palette[schema];

  // No light mode a bolha é clara, então `darker` garante contraste — `dark`
  // do primary é amarelo e ficaria ilegível sobre branco. No dark é o inverso.
  return theme.palette.mode === "light" ? color.darker : color.light;
}
